// src/pages/Achievements.tsx with enhanced filtering functionality
import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowLeftIcon, 
  FunnelIcon, 
  MagnifyingGlassIcon,
  XCircleIcon 
} from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingState from '../components/common/LoadingState';
import Breadcrumb from '../components/achievements/Breadcrumb';
import GroupCard from '../components/achievements/GroupCard';
import CategoryCard from '../components/achievements/CategoryCard';
import AchievementCard from '../components/achievements/AchievementCard';
import SyncProgressButton from '../components/achievements/SyncProgressButton';
import { getGroupedAchievements } from '../services/achievementService';
import { isAuthenticated } from '../services/authService';
import type { Group, Category, Achievement } from '../services/achievementService';
import AchievementFilter, { FilterType } from '../components/achievements/AchievementFilter';
import { useLanguage } from '../contexts/LanguageContext';

const BREADCRUMB_KEY = 'achievementBreadcrumb';
const PATH_KEY = 'achievementPath';


type PathItem = {
  id: number;
  type: 'group' | 'category';
  name: string;
};

const saveBreadcrumb = (path: PathItem[]) =>
  sessionStorage.setItem(BREADCRUMB_KEY, JSON.stringify(path));


const loadBreadcrumb = (): PathItem[] => {
  try {
    const raw = sessionStorage.getItem(BREADCRUMB_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    // Type casting for type property
    return parsed.map((item: any) => ({
      id: item.id,
      type: item.type as 'group' | 'category',
      name: item.name,
    })).filter(
      (item: PathItem) =>
        typeof item.id === 'number' &&
        (item.type === 'group' || item.type === 'category') &&
        typeof item.name === 'string'
    );
  } catch {
    return [];
  }
};

// Find a group by ID 
const findGroupById = (groups: Group[], id: number): Group | null => {
  return groups.find(g => g.id === id) || null;
};

// Find a category by ID (recursively searching through subcategories)
const findCategoryRecursive = (categories: Category[], id: number): Category | null => {
  for (const cat of categories) {
    if (cat.id === id) return cat;
    if (cat.subcategories?.length) {
      const found = findCategoryRecursive(cat.subcategories, id);
      if (found) return found;
    }
  }
  return null;
};

// Find a category in a group by its ID
const findCategoryInGroup = (group: Group, categoryId: number): Category | null => {
  return findCategoryRecursive(group.categories, categoryId);
};

// Find a category's parent group
const findParentGroup = (groups: Group[], categoryId: number): Group | null => {
  for (const group of groups) {
    const found = findCategoryInGroup(group, categoryId);
    if (found) return group;
  }
  return null;
};

// Find all parent categories of a given category
const findCategoryAncestry = (
  rootCategories: Category[],
  targetCategoryId: number,
  currentPath: Category[] = []
): Category[] | null => {
  for (const category of rootCategories) {
    // Check if this is the target category
    if (category.id === targetCategoryId) {
      return [...currentPath, category];
    }

    // Check subcategories if they exist
    if (category.subcategories && category.subcategories.length > 0) {
      const pathFromHere = findCategoryAncestry(
        category.subcategories,
        targetCategoryId,
        [...currentPath, category]
      );
      if (pathFromHere) return pathFromHere;
    }
  }

  return null;
};

// Build a path to a specific category (returns the ancestry chain including all parent categories)
const buildPathToCategory = (
  groups: Group[], 
  categoryId: number
): PathItem[] | null => {
  // Find the parent group first
  const parentGroup = findParentGroup(groups, categoryId);
  if (!parentGroup) return null;
  
  // Start path with the group
  const path: PathItem[] = [
    { id: parentGroup.id, type: 'group', name: parentGroup.display_name }
  ];
  
  // Find the full category ancestry path within the group
  const categoryAncestry = findCategoryAncestry(parentGroup.categories, categoryId);
  if (!categoryAncestry || categoryAncestry.length === 0) {
    return path; // Return just the group if category ancestry not found
  }
  
  // Add each category in the ancestry to the path
  categoryAncestry.forEach(category => {
    path.push({ 
      id: category.id, 
      type: 'category', 
      name: category.display_name 
    });
  });
  
  return path;
};

const navigateFromUrlParams = (
  groups: Group[],
  groupId: string | null, 
  categoryId: string | null,
  setActiveGroup: React.Dispatch<React.SetStateAction<Group | null>>,
  setVisibleCategories: React.Dispatch<React.SetStateAction<Category[]>>,
  setVisibleAchievements: React.Dispatch<React.SetStateAction<Achievement[]>>,
  setCurrentView: React.Dispatch<React.SetStateAction<'groups' | 'categories' | 'achievements'>>,
  setCurrentPath: React.Dispatch<React.SetStateAction<PathItem[]>>
): boolean => {
  // If we have a category ID, try to navigate directly to it
  if (categoryId) {
    const catId = parseInt(categoryId, 10);
    if (isNaN(catId)) return false;
    
    // Find the path to this category
    const path = buildPathToCategory(groups, catId);
    if (!path) return false;
    
    // Set the active group first
    const group = findGroupById(groups, path[0].id);
    if (!group) return false;
    
    setActiveGroup(group);
    setCurrentPath(path);
    saveBreadcrumb(path);
    
    // Find the category and set visible content
    const category = findCategoryInGroup(group, catId);
    if (!category) {
      // If category not found, just show the group's categories
      setVisibleCategories(group.categories);
      setCurrentView('categories');
      return true;
    }
    
    // Need to make a distinction between:
    // 1. The last category in the path (which we need to show content for)
    // 2. Intermediate categories in the path (which we just need to navigate through)
    
    // Check if this is the target category (last in the path)
    const isTargetCategory = path.length > 0 && path[path.length - 1].id === category.id;
    
    if (isTargetCategory) {
      // If this is our destination category, show its content
      if (category.achievements?.length) {
        setVisibleAchievements(category.achievements);
        setCurrentView('achievements');
      } else if (category.subcategories?.length) {
        setVisibleCategories(category.subcategories);
        setCurrentView('categories');
      } else {
        setVisibleAchievements([]);
        setCurrentView('achievements');
      }
    } else {
      // If this is an intermediate category, we just include it in the path
      // The navigation to the target category will continue in the next iteration
    }
    
    return true;
  }
  // If we have only a group ID, navigate to that group
  else if (groupId) {
    const grpId = parseInt(groupId, 10);
    if (isNaN(grpId)) return false;
    
    const group = findGroupById(groups, grpId);
    if (!group) return false;
    
    setActiveGroup(group);
    const path = [{ id: group.id, type: 'group' as const, name: group.display_name }];
    setCurrentPath(path);
    saveBreadcrumb(path);
    setVisibleCategories(group.categories);
    setCurrentView('categories');
    
    return true;
  }
  
  return false;
}

// Helper function to filter items based on completion status
const applyFilter = <T extends { is_completed?: boolean }>(
  items: T[] | null | undefined,
  filterType: FilterType
): T[] => {
  // Sicherstellen, dass items ein Array ist
  if (!items || !Array.isArray(items)) {
    console.warn('applyFilter: Expected array but got', typeof items);
    return [];
  }
  
  if (filterType === 'all') {
    return items;
  } else if (filterType === 'completed') {
    return items.filter(item => Boolean(item.is_completed));
  } else { // incomplete
    return items.filter(item => !item.is_completed);
  }
};

// Helper function to count completed items
const countCompletedItems = <T extends { is_completed?: boolean }>(
  items: T[]
): number => {
  return items.filter(item => item.is_completed).length;
};

const Achievements: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<PathItem[]>([]);
  const [currentView, setCurrentView] = useState<'groups' | 'categories' | 'achievements'>('groups');
  const [activeGroup, setActiveGroup] = useState<Group | null>(null);
  const [visibleAchievements, setVisibleAchievements] = useState<Achievement[]>([]);
  const [visibleCategories, setVisibleCategories] = useState<Category[]>([]);
  const [userIsAuthenticated, setUserIsAuthenticated] = useState(false);
  
  // Search & filter state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [achievementFilter, setAchievementFilter] = useState<FilterType>('all');
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [navigationAttempted, setNavigationAttempted] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reset') === '1') {
      sessionStorage.removeItem(PATH_KEY);
      sessionStorage.removeItem(BREADCRUMB_KEY);
      window.history.replaceState({}, '', '/achievements');
    }
    setUserIsAuthenticated(isAuthenticated());
  }, [location]);

  // Apply filter to visible achievements
  const filteredAchievements = React.useMemo(() => {
    try {
      return applyFilter(visibleAchievements, achievementFilter);
    } catch (error) {
      console.error('Error filtering achievements:', error);
      return [];
    }
  }, [visibleAchievements, achievementFilter]);

  const filteredCategories = React.useMemo(() => {
  try {
    if (!visibleCategories || !Array.isArray(visibleCategories)) {
      console.warn('filteredCategories: Expected array but got', typeof visibleCategories);
      return [];
    }
    
    // Wenn nicht authentifiziert oder Filter auf "Alle" gesetzt, gib alle Kategorien zurück
    if (!userIsAuthenticated || achievementFilter === 'all') {
      return visibleCategories;
    }
    
    // Für Kategorien nach Achievements filtern
    return visibleCategories.filter(category => {
      // Wenn keine Achievements, Kategorie immer einschließen
      if (!category.achievements || !Array.isArray(category.achievements) || category.achievements.length === 0) {
        return true;
      }
      
      const totalAchievements = category.achievements.length;
      const completedAchievements = category.achievements.filter(a => Boolean(a.is_completed)).length;
      
      if (achievementFilter === 'completed') {
        // Bei "Completed" nur Kategorien einschließen, wo alle Achievements abgeschlossen sind
        return completedAchievements === totalAchievements && totalAchievements > 0;
      } else { // incomplete
        // Bei "Incomplete" nur Kategorien einschließen, wo mindestens ein Achievement nicht abgeschlossen ist
        return completedAchievements < totalAchievements;
      }
    });
  } catch (error) {
    console.error('Error filtering categories:', error);
    return [];
  }
}, [visibleCategories, achievementFilter, userIsAuthenticated]);
  // Apply filter to group view
  const filteredGroups = React.useMemo(() => {
    try {
      if (!groups || !Array.isArray(groups)) {
        console.warn('filteredGroups: Expected array but got', typeof groups);
        return [];
      }
      
      // Wenn nicht authentifiziert oder Filter auf "Alle" gesetzt, gib alle Gruppen zurück
      if (!userIsAuthenticated || achievementFilter === 'all') {
        return groups;
      }
      
      // Für Gruppen nach Achievements filtern
      return groups.filter(group => {
        // Sammle alle Achievements aus dieser Gruppe und allen Unterkategorien
        const allAchievements: Achievement[] = [];
        
        if (group.categories && Array.isArray(group.categories)) {
          group.categories.forEach(cat => {
            if (cat.achievements && Array.isArray(cat.achievements)) {
              allAchievements.push(...cat.achievements);
            }
            
            // Auch Achievements aus Unterkategorien einschließen
            if (cat.subcategories && Array.isArray(cat.subcategories)) {
              cat.subcategories.forEach(subcat => {
                if (subcat.achievements && Array.isArray(subcat.achievements)) {
                  allAchievements.push(...subcat.achievements);
                }
              });
            }
          });
        }
        
        // Wenn keine Achievements, Gruppe immer einschließen
        if (allAchievements.length === 0) {
          return true;
        }
        
        const totalAchievements = allAchievements.length;
        const completedAchievements = allAchievements.filter(a => Boolean(a.is_completed)).length;
        
        if (achievementFilter === 'completed') {
          // Bei "Completed" nur Gruppen einschließen, wo alle Achievements abgeschlossen sind
          return completedAchievements === totalAchievements && totalAchievements > 0;
        } else { // incomplete
          // Bei "Incomplete" nur Gruppen einschließen, wo mindestens ein Achievement nicht abgeschlossen ist
          return completedAchievements < totalAchievements;
        }
      });
    } catch (error) {
      console.error('Error filtering groups:', error);
      return [];
    }
  }, [groups, achievementFilter, userIsAuthenticated]);
  
  // Stats for achievement filter
  const achievementStats = React.useMemo(() => {
    const total = visibleAchievements.length;
    const completed = countCompletedItems(visibleAchievements);
    return { total, completed };
  }, [visibleAchievements]);
  
  // Stats for category filter
  const categoryStats = React.useMemo(() => {
    const total = visibleCategories.length;
    const completed = visibleCategories.filter(category => {
      if (!category.achievements || category.achievements.length === 0) {
        return false;
      }
      const completedAchievements = countCompletedItems(category.achievements);
      return completedAchievements === category.achievements.length;
    }).length;
    return { total, completed };
  }, [visibleCategories]);
  
  // Stats for group filter
  const groupStats = React.useMemo(() => {
    const total = groups.length;
    const completed = groups.filter(group => {
      // Collect all achievements in this group
      const achievements: Achievement[] = [];
      
      group.categories.forEach(cat => {
        if (cat.achievements) {
          achievements.push(...cat.achievements);
        }
        
        // Also include achievements from subcategories
        if (cat.subcategories) {
          cat.subcategories.forEach(subcat => {
            if (subcat.achievements) {
              achievements.push(...subcat.achievements);
            }
          });
        }
      });
      
      if (achievements.length === 0) {
        return false;
      }
      
      const completedAchievements = countCompletedItems(achievements);
      return completedAchievements === achievements.length;
    }).length;
    return { total, completed };
  }, [groups]);

  // Collect all achievements from all categories recursively
  const collectAllAchievements = useCallback((groups: Group[]): Achievement[] => {
    let achievements: Achievement[] = [];
    
    const processGroup = (group: Group) => {
      group.categories.forEach(category => {
        if (category.achievements?.length) {
          achievements = [...achievements, ...category.achievements];
        }
        
        if (category.subcategories?.length) {
          category.subcategories.forEach(subcategory => {
            if (subcategory.achievements?.length) {
              achievements = [...achievements, ...subcategory.achievements];
            }
          });
        }
      });
    };
    
    groups.forEach(processGroup);
    return achievements;
  }, []);
  
  const loadAchievementData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getGroupedAchievements();
      setGroups(data);
      
      // Collect all achievements for global search
      const allAchievementsList = collectAllAchievements(data);
      setAllAchievements(allAchievementsList);

      // First, check URL parameters for direct navigation
      const params = new URLSearchParams(location.search);
      const groupParam = params.get('group');
      const categoryParam = params.get('category');

      let navigationSuccessful = false;
      
      // Try direct navigation from URL parameters
      if ((groupParam || categoryParam) && !navigationAttempted) {
        navigationSuccessful = navigateFromUrlParams(
          data,
          groupParam,
          categoryParam,
          setActiveGroup,
          setVisibleCategories,
          setVisibleAchievements,
          setCurrentView,
          setCurrentPath
        );
        
        // Mark that we've attempted navigation regardless of success
        setNavigationAttempted(true);
      }
      
      // If URL navigation failed or wasn't attempted, try to restore from session
      if (!navigationSuccessful && !navigationAttempted) {
        const breadcrumb = loadBreadcrumb();
        const groupItem = breadcrumb.find(p => p.type === 'group');
        const group = data.find(g => g.id === groupItem?.id);
        if (group) {
          await restorePath(
            group,
            breadcrumb,
            setActiveGroup,
            setVisibleCategories,
            setVisibleAchievements,
            setCurrentView,
            setCurrentPath
          );
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load achievements');
    } finally {
      setLoading(false);
    }
  }, [location.search, navigationAttempted, setLoading, setGroups, setError, collectAllAchievements]);

  useEffect(() => {
    loadAchievementData();
    const handleProgressSync = () => {
      loadAchievementData();
    };
    window.addEventListener('progress-sync-completed', handleProgressSync);
    return () => window.removeEventListener('progress-sync-completed', handleProgressSync);
  }, [location, loadAchievementData]);

  const restorePath = async (
    group: Group,
    breadcrumb: PathItem[],
    setActiveGroup: React.Dispatch<React.SetStateAction<Group | null>>,
    setVisibleCategories: React.Dispatch<React.SetStateAction<Category[]>>,
    setVisibleAchievements: React.Dispatch<React.SetStateAction<Achievement[]>>,
    setCurrentView: React.Dispatch<React.SetStateAction<'groups' | 'categories' | 'achievements'>>,
    setCurrentPath: React.Dispatch<React.SetStateAction<PathItem[]>>
  ) => {
    let categories = group.categories;
    const updatedPath: PathItem[] = [];
    
    for (const item of breadcrumb) {
      if (item.type === 'group') {
        setActiveGroup(group);
        setVisibleCategories(group.categories);
        setCurrentView('categories');
        updatedPath.push({ id: group.id, type: 'group', name: group.display_name });
        setCurrentPath([...updatedPath]);
      } else if (item.type === 'category') {
        const category = findCategoryRecursive(categories, item.id);
        if (!category) return;
        
        updatedPath.push({ id: category.id, type: 'category', name: category.display_name });
        setCurrentPath([...updatedPath]);
        
        if (category.achievements?.length) {
          setVisibleAchievements(category.achievements);
          setCurrentView('achievements');
        } else if (category.subcategories?.length) {
          setVisibleCategories(category.subcategories);
          setCurrentView('categories');
          categories = category.subcategories;
        } else {
          setVisibleAchievements([]);
          setCurrentView('achievements');
        }
      }
    }
    saveBreadcrumb(updatedPath);
  };

  const handleGoBack = () => {
    const savedPath = sessionStorage.getItem('achievementPath');
    navigate(savedPath || '/achievements');
  };

  const handleGroupClick = (group: Group) => {
    // Exit search mode when navigating
    setIsSearchMode(false);
    
    setActiveGroup(group);
    setCurrentPath([{ id: group.id, type: 'group', name: group.display_name }]);
    setVisibleCategories(group.categories);
    setCurrentView('categories');
    
    // Reset search and filter when navigating
    setSearchQuery('');
    setAchievementFilter('all');
    
    // Update URL
    navigate(`/achievements?group=${group.id}`, { replace: true });
  };

  const handleCategoryClick = (category: Category) => {
    // Exit search mode when navigating
    setIsSearchMode(false);
    
    const newPath: PathItem[] = [
      ...currentPath,
      { id: category.id, type: 'category', name: category.display_name }
    ];
    setCurrentPath(newPath);
    saveBreadcrumb(newPath);
    
    // Reset search and filter when navigating
    setSearchQuery('');
    setAchievementFilter('all');
    
    if (category.achievements?.length) {
      setVisibleAchievements(category.achievements);
      setCurrentView('achievements');
    } else if (category.subcategories?.length) {
      setVisibleCategories(category.subcategories);
      setCurrentView('categories');
    } else {
      setVisibleAchievements([]);
      setCurrentView('achievements');
    }
    
    // Update URL to include both group and category
    if (activeGroup) {
      navigate(`/achievements?group=${activeGroup.id}&category=${category.id}`, { replace: true });
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    // Exit search mode when using breadcrumb navigation
    setIsSearchMode(false);
    
    if (index === -1) {
      setActiveGroup(null);
      setCurrentView('groups');
      setCurrentPath([]);
      window.history.pushState(null, '', location.pathname);
      saveBreadcrumb([]);
      
      // Reset search and filter when navigating home
      setSearchQuery('');
      setAchievementFilter('all');
      return;
    }
    
    const newPath = currentPath.slice(0, index + 1) as PathItem[];
    setCurrentPath(newPath);
    saveBreadcrumb(newPath);
    
    // Reset search and filter when navigating
    setSearchQuery('');
    setAchievementFilter('all');
    
    const last = newPath[newPath.length - 1];
    if (last.type === 'group') {
      const group = groups.find(g => g.id === last.id);
      if (!group) return;
      
      setActiveGroup(group);
      setVisibleCategories(group.categories);
      setCurrentView('categories');
      navigate(`/achievements?group=${group.id}`, { replace: true });
    } else {
      if (!activeGroup) return;
      
      const category = findCategoryRecursive(activeGroup.categories, last.id);
      if (!category) return;
      
      if (category.achievements?.length) {
        setVisibleAchievements(category.achievements);
        setCurrentView('achievements');
      } else if (category.subcategories?.length) {
        setVisibleCategories(category.subcategories);
        setCurrentView('categories');
      } else {
        setVisibleAchievements([]);
        setCurrentView('achievements');
      }
      
      navigate(`/achievements?group=${activeGroup.id}&category=${category.id}`, { replace: true });
    }
  };
  
  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Enter search mode if there's a query
    if (value.trim()) {
      setIsSearchMode(true);
    }
  };
  
  // Start search with button
  const startSearch = () => {
    if (searchQuery.trim()) {
      setIsSearchMode(true);
    }
  };
  
  // Clear search query and exit search mode
  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
  };
  
  // Apply search to all achievements (global search)
  const searchedAchievements = React.useMemo(() => {
    if (!searchQuery.trim() || !isSearchMode) {
      return visibleAchievements;
    }
    
    const query = searchQuery.toLowerCase();
    // When in search mode, search through all achievements instead of just visible ones
    const achievementsToSearch = isSearchMode ? allAchievements : visibleAchievements;
    
    return achievementsToSearch.filter(achievement => 
      achievement.name.toLowerCase().includes(query) || 
      achievement.description.toLowerCase().includes(query)
    );
  }, [visibleAchievements, allAchievements, searchQuery, isSearchMode]);
  
  // Calculate completion statistics (based on filtered/searched achievements)
  const completionStats = React.useMemo(() => {
    const total = searchedAchievements.length;
    
    // Verbesserte Logik zur Erkennung abgeschlossener Achievements
    const completed = searchedAchievements.filter(a => {
      // Prüfe auf verschiedene Möglichkeiten, dass ein Achievement abgeschlossen ist
      return a.is_completed === true || 
             (a.progress && a.progress.percentage === 100) || 
             (a.progress && a.progress.current >= a.progress.total);
    }).length;
    
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return {
      total,
      completed,
      percentage,
      isComplete: total > 0 && completed === total
    };
  }, [searchedAchievements]);

  // Generate filter label for the current filter
  const getFilterLabel = () => {
    switch(achievementFilter) {
      case 'completed': return t('achievements.showCompletedOnly');
      case 'incomplete': return t('achievements.showIncompleteOnly');
      default: return t('common.filter');
    }
  };

  if (loading) return <LoadingState message={t('common.loading')} />;
  if (error || !groups.length) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-destiny-red text-xl mb-4">{t('common.error')}</h2>
        <p className="text-gray-300">{error || t('achievements.notFound')}</p>
        <button onClick={handleGoBack} className="inline-flex items-center mb-6 text-destiny-blue hover:text-destiny-gold">
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          {t('common.back')} {t('achievements.title')}
        </button>
      </div>
    );
  }

  // RenderFilter function - to reuse filter UI across different views
  const renderFilter = (currentView: 'groups' | 'categories' | 'achievements') => {
    if (!userIsAuthenticated) return null;
    
    let stats;
    switch (currentView) {
      case 'groups':
        stats = groupStats;
        break;
      case 'categories':
        stats = categoryStats;
        break;
      case 'achievements':
        stats = achievementStats;
        break;
    }
    
    return (
      <AchievementFilter
        currentFilter={achievementFilter}
        onFilterChange={setAchievementFilter}
        totalCount={stats.total}
        completedCount={stats.completed}
      />
    );
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-destiny-gold">{t('achievements.title')}</h1>
          <div className="flex space-x-2">
            {/* Sync Button nur für authentifizierte Benutzer anzeigen */}
            {userIsAuthenticated && (
              <SyncProgressButton onSyncComplete={loadAchievementData} />
            )}
          </div>
        </div>
        
        {/* Global Search Bar - Always visible except on achievement detail */}
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('achievements.searchPlaceholder')}
              className="block w-full pl-10 pr-10 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-destiny-gold"
              onKeyDown={(e) => e.key === 'Enter' && startSearch()}
            />
            {searchQuery && (
              <button 
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          
          <button
            onClick={startSearch}
            className="bg-destiny-blue text-white px-4 py-2 rounded inline-flex items-center justify-center hover:bg-opacity-90"
            disabled={!searchQuery.trim()}
          >
            {t('achievements.search')}
          </button>
          
          {isSearchMode && (
            <button
              onClick={clearSearch}
              className="bg-gray-600 text-white px-4 py-2 rounded inline-flex items-center justify-center hover:bg-gray-500"
            >
              {t('achievements.clearSearch')}
            </button>
          )}
        </div>
        
        {/* Search Status */}
        {isSearchMode && (
          <div className="mt-3 text-sm flex justify-between items-center">
            <span className="text-destiny-gold">
              {searchedAchievements.length > 0 
                ? t('achievements.searchResults')
                  .replace('{count}', searchedAchievements.length.toString())
                  .replace('{total}', allAchievements.length.toString())
                : t('achievements.noSearchResults').replace('{query}', searchQuery)
              }
            </span>
            
            {userIsAuthenticated && searchedAchievements.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowFilterOptions(!showFilterOptions)}
                  className={`bg-gray-700 ${achievementFilter !== 'all' ? 'text-destiny-gold' : 'text-white'} px-3 py-1 rounded inline-flex items-center hover:bg-gray-600`}
                >
                  <FunnelIcon className="h-4 w-4 mr-1" />
                  <span>{getFilterLabel()}</span>
                </button>
                
                {showFilterOptions && (
                  <div className="absolute right-0 mt-1 w-56 rounded-md shadow-lg bg-gray-700 ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <button
                        onClick={() => {
                          setAchievementFilter('all');
                          setShowFilterOptions(false);
                        }}
                        className={`block px-4 py-2 text-sm w-full text-left ${
                          achievementFilter === 'all' ? 'bg-gray-600 text-destiny-gold' : 'text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {t('achievements.showAll')}
                      </button>
                      <button
                        onClick={() => {
                          setAchievementFilter('completed');
                          setShowFilterOptions(false);
                        }}
                        className={`block px-4 py-2 text-sm w-full text-left ${
                          achievementFilter === 'completed' ? 'bg-gray-600 text-destiny-gold' : 'text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {t('achievements.showCompletedOnly')}
                      </button>
                      <button
                        onClick={() => {
                          setAchievementFilter('incomplete');
                          setShowFilterOptions(false);
                        }}
                        className={`block px-4 py-2 text-sm w-full text-left ${
                          achievementFilter === 'incomplete' ? 'bg-gray-600 text-destiny-gold' : 'text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {t('achievements.showIncompleteOnly')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
  
      <Breadcrumb path={currentPath} onClick={handleBreadcrumbClick} />
      
      {/* Filter Controls */}
      {!isSearchMode && (
        <div className="flex justify-between items-center mb-4">
          <div className="flex-grow"></div>
          {renderFilter(currentView)}
        </div>
      )}
      
      {/* Achievement Completion Stats Banner */}
      {((currentView === 'achievements' && visibleAchievements.length > 0) || 
         (isSearchMode && searchedAchievements.length > 0)) && 
       userIsAuthenticated && (
        <div className={`mb-6 rounded-lg p-4 ${
          completionStats.isComplete ? 'bg-destiny-green bg-opacity-20 border border-destiny-green' : 'bg-gray-800'
        }`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className={`font-medium ${completionStats.isComplete ? 'text-destiny-green' : 'text-white'}`}>
                {completionStats.isComplete ? t('achievements.allCompleted') : t('achievements.partiallyCompleted')}
              </h3>
              <p className="text-gray-300 text-sm">
                {completionStats.completed} / {completionStats.total} {t('achievements.title')}
                {' '}({completionStats.percentage}%)
              </p>
            </div>
            
            {/* Progress bar */}
            <div className="w-1/3">
              <div className="w-full bg-gray-700 rounded-full h-2.5">
                <div
                  className={`${completionStats.isComplete ? 'bg-destiny-green' : 'bg-destiny-blue'} h-2.5 rounded-full`}
                  style={{ width: `${completionStats.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}
  
      {/* Search Results View */}
      {isSearchMode && (
        <div>
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <h2 className="text-xl font-bold text-white mb-2">{t('achievements.searchResultsTitle')}</h2>
            {searchedAchievements.length === 0 && (
              <p className="text-gray-300">
                {t('achievements.noSearchResults').replace('{query}', searchQuery)}
              </p>
            )}
          </div>
          
          {searchedAchievements.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAchievements.map(achievement => (
                <AchievementCard
                  key={achievement.id}
                  achievement={achievement}
                  showProgress={userIsAuthenticated}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Normal Views (non-search mode) */}
      {!isSearchMode && (
        <>
          {/* Grid views for each content type */}
          {currentView === 'groups' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map(group => (
                <GroupCard 
                  key={group.id} 
                  group={group} 
                  onClick={() => handleGroupClick(group)}
                  userIsAuthenticated={userIsAuthenticated} // Pass auth status
                />
              ))}
            </div>
          )}
      
          {currentView === 'categories' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCategories.map(category => (
                <CategoryCard 
                  key={category.id} 
                  category={category} 
                  onClick={() => handleCategoryClick(category)}
                  userIsAuthenticated={userIsAuthenticated} // Pass auth status
                />
              ))}
            </div>
          )}
          
          {/* Achievements view */}  
          {currentView === 'achievements' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAchievements.length > 0 ? (
                filteredAchievements.map(achievement => (
                  <AchievementCard
                    key={achievement.id}
                    achievement={achievement}
                    showProgress={userIsAuthenticated}
                  />
                ))
              ) : (
                <div className="col-span-full bg-gray-800 rounded-lg p-6 text-center">
                  <p className="text-gray-300">
                    {achievementFilter === 'completed' 
                      ? t('achievements.noCompletedAchievements')
                      : achievementFilter === 'incomplete'
                        ? t('achievements.noIncompleteAchievements')
                        : t('achievements.noAchievements')}
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Achievements;